import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../../components/UI/Button';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentGraphVisualEditor } from '../../../components/AgentPlatform/AgentGraphVisualEditor';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformTabs } from '../../../components/AgentPlatform/AgentPlatformTabs';
import { AgentPlatformPanel } from '../../../components/AgentPlatform/AgentPlatformPanel';
import { apTheme } from '../../../utils/agentPlatformTheme';
import { AgentPlaygroundPanel } from '../../../components/AgentPlatform/AgentPlaygroundPanel';
import { AgentPublishModal } from '../../../components/AgentPlatform/AgentPublishModal';
import { JsonTextArea } from '../../../components/AgentPlatform/JsonTextArea';
import { StatusBadge } from '../../../components/AgentPlatform/StatusBadge';
import { useToast } from '../../../components/UI/ToastProvider';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type {
  AgentDetailDto,
  AgentFlowDto,
  AgentToolDto,
  AgentKnowledgeBaseDto,
  AgentChannel,
} from '../../../types/agentPlatform';
import { labelAgentStatus, labelAgentType } from '../../../utils/agentPlatformLabels';

type TabId = 'geral' | 'vinculos' | 'grafo' | 'playground' | 'canais';

const tabs: { id: TabId; label: string }[] = [
  { id: 'geral', label: 'Geral' },
  { id: 'vinculos', label: 'Vínculos' },
  { id: 'grafo', label: 'Orquestração' },
  { id: 'playground', label: 'Playground' },
  { id: 'canais', label: 'Canais' },
];

export const AgentEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabId>('geral');
  const [agent, setAgent] = useState<AgentDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'AI' as AgentDetailDto['type'],
    systemPrompt: '',
    chatProvider: 'openai',
    llmModel: 'gpt-4o-mini',
    apiKey: '',
    chatApiBaseUrl: '',
    temperature: '',
    maxTokens: '',
  });

  const [graphJson, setGraphJson] = useState('{}');
  const [graphMeta, setGraphMeta] = useState<{ version?: number; isPublished: boolean }>({
    isPublished: false,
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [graphViewMode, setGraphViewMode] = useState<'visual' | 'json'>('visual');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [allTools, setAllTools] = useState<AgentToolDto[]>([]);
  const [allFlows, setAllFlows] = useState<AgentFlowDto[]>([]);
  const [bindToolId, setBindToolId] = useState('');
  const [bindFlowId, setBindFlowId] = useState('');
  const [bindFlowAlias, setBindFlowAlias] = useState('');
  const [allKnowledgeBases, setAllKnowledgeBases] = useState<AgentKnowledgeBaseDto[]>([]);
  const [bindKbId, setBindKbId] = useState('');

  const loadAgent = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await agentPlatformService.getAgent(id);
      setAgent(data);
      setForm({
        name: data.name,
        description: data.description ?? '',
        type: data.type,
        systemPrompt: data.systemPrompt ?? '',
        chatProvider: data.chatProvider ?? 'openai',
        llmModel: data.llmModel ?? 'gpt-4o-mini',
        apiKey: data.apiKey ?? '',
        chatApiBaseUrl: data.chatApiBaseUrl ?? '',
        temperature: data.temperature != null ? String(data.temperature) : '',
        maxTokens: data.maxTokens != null ? String(data.maxTokens) : '',
      });
    } catch {
      addToast('Assistente não encontrado.', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  const loadGraph = useCallback(async () => {
    if (!id) return;
    try {
      const g = await agentPlatformService.getGraph(id);
      setGraphJson(g.definition);
      setGraphMeta({
        version: g.currentVersionNumber ?? undefined,
        isPublished: g.isPublished,
      });
    } catch {
      addToast('Não foi possível carregar o grafo.', 'error');
    }
  }, [id, addToast]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  useEffect(() => {
    if (activeTab === 'grafo') {
      loadGraph();
    }
  }, [activeTab, loadGraph]);

  useEffect(() => {
    if (activeTab !== 'vinculos') return;
    agentPlatformService.getTools().then(setAllTools).catch(() => undefined);
    agentPlatformService.getFlows().then(setAllFlows).catch(() => undefined);
    agentPlatformService.getKnowledgeBases().then(setAllKnowledgeBases).catch(() => undefined);
  }, [activeTab]);

  const saveGeneral = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await agentPlatformService.updateAgent(id, {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        type: form.type,
        systemPrompt: form.systemPrompt || undefined,
        chatProvider: form.chatProvider || undefined,
        llmModel: form.llmModel || undefined,
        apiKey: form.apiKey,
        chatApiBaseUrl: form.chatApiBaseUrl.trim() || undefined,
        temperature: form.temperature ? Number(form.temperature) : undefined,
        maxTokens: form.maxTokens ? Number(form.maxTokens) : undefined,
      });
      setAgent(updated);
      addToast('Assistente salvo.', 'success');
    } catch {
      addToast('Erro ao salvar assistente.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmPublish = async (channels: AgentChannel[], changeNotes: string) => {
    if (!id) return;
    setPublishing(true);
    try {
      const updated = await agentPlatformService.publishAgent(id, {
        channels,
        changeNotes: changeNotes.trim() || 'Publicado via admin',
      });
      setAgent(updated);
      setShowPublishModal(false);
      addToast('Assistente publicado.', 'success');
    } catch {
      addToast('Erro ao publicar. Verifique grafo e vínculos.', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const saveGraph = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const g = await agentPlatformService.updateGraph(id, { definition: graphJson });
      setGraphJson(g.definition);
      setGraphMeta({ version: g.currentVersionNumber ?? undefined, isPublished: g.isPublished });
      addToast('Grafo salvo.', 'success');
    } catch {
      addToast('Grafo inválido ou erro ao salvar.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const validateGraph = async () => {
    if (!id) return;
    try {
      const result = await agentPlatformService.validateGraph(id, graphJson);
      setValidationErrors(result.errors.map(e => e.message));
      if (result.isValid) {
        addToast('Grafo válido.', 'success');
      } else {
        addToast('Grafo com erros de validação.', 'error');
      }
    } catch {
      addToast('Não foi possível validar.', 'error');
    }
  };

  const bindTool = async () => {
    if (!id || !bindToolId) return;
    try {
      const updated = await agentPlatformService.bindTool(id, {
        agentToolId: bindToolId,
        priority: 10,
      });
      setAgent(updated);
      setBindToolId('');
      addToast('Ferramenta vinculada.', 'success');
    } catch {
      addToast('Erro ao vincular ferramenta.', 'error');
    }
  };

  const bindFlow = async () => {
    if (!id || !bindFlowId) return;
    try {
      const updated = await agentPlatformService.bindFlow(id, {
        agentFlowId: bindFlowId,
        alias: bindFlowAlias.trim() || undefined,
        priority: 10,
      });
      setAgent(updated);
      setBindFlowId('');
      setBindFlowAlias('');
      addToast('Fluxo vinculado.', 'success');
    } catch {
      addToast('Erro ao vincular fluxo.', 'error');
    }
  };

  const bindKnowledgeBase = async () => {
    if (!id || !bindKbId) return;
    try {
      const updated = await agentPlatformService.bindKnowledgeBase(id, {
        agentKnowledgeBaseId: bindKbId,
        priority: 10,
      });
      setAgent(updated);
      setBindKbId('');
      addToast('Base de conhecimento vinculada.', 'success');
    } catch {
      addToast('Erro ao vincular base.', 'error');
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-500">Carregando assistente…</div>;
  }

  if (!agent) {
    return (
      <div className="py-16 text-center">
        <p className="text-gray-600 mb-4">Assistente não encontrado.</p>
        <Link to="/agent-platform/agents" className="text-blue-700 hover:underline">
          Voltar à lista
        </Link>
      </div>
    );
  }

  return (
    <AgentPlatformFeatureGate feature="agents">
      <AgentPlatformAdminGate>
        <AgentPlatformShell
          variant={activeTab === 'grafo' || activeTab === 'playground' ? 'studio' : 'default'}
        >
          <AgentPlatformPageHeader
            backTo="/agent-platform/agents"
            backLabel="Assistente de IA"
            title={agent.name}
            area={activeTab === 'grafo' ? 'graph' : 'assistants'}
            subtitle={`${agent.code}${agent.currentVersionNumber != null ? ` · v${agent.currentVersionNumber}` : ''}`}
            meta={
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <StatusBadge
                  status={agent.status}
                  label={labelAgentStatus(agent.status)}
                  kind="agent"
                />
                <span className="text-xs text-slate-500">{labelAgentType(agent.type)}</span>
              </div>
            }
            actions={
              activeTab === 'geral' ? (
                <>
                  <Button onClick={saveGeneral} disabled={saving}>
                    Salvar rascunho
                  </Button>
                  <Button variant="secondary" onClick={() => setShowPublishModal(true)}>
                    Publicar
                  </Button>
                </>
              ) : undefined
            }
          />

          <div className="mb-6">
            <AgentPlatformTabs
              tabs={tabs}
              active={activeTab}
              onChange={setActiveTab}
              variant={activeTab === 'grafo' ? 'studio' : 'default'}
            />
          </div>

        {activeTab === 'geral' && (
          <AgentPlatformPanel title="Configuração do assistente" description="Prompt, provedor LLM e parâmetros por agente (agnóstico de tenant).">
            <div className="max-w-3xl space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
              <input
                className={apTheme.input}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
              <textarea
                className={apTheme.input}
                rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
              <select
                className={apTheme.input}
                value={form.type}
                onChange={e =>
                  setForm(f => ({ ...f, type: e.target.value as AgentDetailDto['type'] }))
                }
              >
                <option value="AI">IA</option>
                <option value="Automation">Automação</option>
                <option value="Hybrid">Híbrido</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">{labelAgentType(form.type)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">System prompt</label>
              <textarea
                className={`${apTheme.input} font-mono text-sm`}
                rows={8}
                value={form.systemPrompt}
                onChange={e => setForm(f => ({ ...f, systemPrompt: e.target.value }))}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Provedor LLM</label>
                <select
                  className={apTheme.input}
                  value={form.chatProvider}
                  onChange={e => setForm(f => ({ ...f, chatProvider: e.target.value }))}
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="gemini">Google Gemini</option>
                  <option value="ollama">Ollama</option>
                  <option value="local">Local</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
                <select
                  className={apTheme.input}
                  value={form.llmModel}
                  onChange={e => setForm(f => ({ ...f, llmModel: e.target.value }))}
                >
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                  <option value="claude-3-haiku">Claude 3 Haiku</option>
                  <option value="gemini-pro">Gemini Pro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
                <input
                  type="password"
                  className={apTheme.input}
                  value={form.apiKey}
                  onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
                  placeholder="sk-…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL base (OpenAI-compatible)</label>
                <input
                  className={apTheme.input}
                  value={form.chatApiBaseUrl}
                  onChange={e => setForm(f => ({ ...f, chatApiBaseUrl: e.target.value }))}
                  placeholder="https://api.openai.com/v1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Temperature</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  className={apTheme.input}
                  value={form.temperature}
                  onChange={e => setForm(f => ({ ...f, temperature: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Max tokens</label>
                <input
                  type="number"
                  className={apTheme.input}
                  value={form.maxTokens}
                  onChange={e => setForm(f => ({ ...f, maxTokens: e.target.value }))}
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Cada agente usa seu próprio provedor e credenciais. Se a API Key estiver vazia, o runtime usa a configuração global da aplicação.
            </p>
            </div>
          </AgentPlatformPanel>
        )}

        {activeTab === 'vinculos' && (
          <div className="space-y-6">
            <AgentPlatformPanel title="Ferramentas">
              <ul className="divide-y divide-slate-100 mb-4">
                {agent.tools.length === 0 && (
                  <li className="py-2 text-sm text-slate-500">Nenhuma ferramenta vinculada.</li>
                )}
                {agent.tools.map(t => (
                  <li key={t.id} className="py-2 flex justify-between items-center text-sm">
                    <span className="text-slate-800">
                      {t.toolName ?? t.toolCode}{' '}
                      <span className="text-slate-400">#{t.priority}</span>
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={async () => {
                        if (!id) return;
                        const updated = await agentPlatformService.unbindTool(id, t.id);
                        setAgent(updated);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <select
                  className={`flex-1 ${apTheme.input}`}
                  value={bindToolId}
                  onChange={e => setBindToolId(e.target.value)}
                >
                  <option value="">Selecione uma ferramenta…</option>
                  {allTools.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.code})
                    </option>
                  ))}
                </select>
                <Button onClick={bindTool} disabled={!bindToolId}>
                  Vincular
                </Button>
              </div>
            </AgentPlatformPanel>

            <AgentPlatformPanel title="Fluxos (automação)">
              <ul className="divide-y divide-slate-100 mb-4">
                {agent.flows.length === 0 && (
                  <li className="py-2 text-sm text-slate-500">Nenhum fluxo vinculado.</li>
                )}
                {agent.flows.map(f => (
                  <li key={f.id} className="py-2 flex justify-between items-center text-sm">
                    <span className="text-slate-800">
                      {f.flowName ?? f.flowCode}
                      {f.alias && <span className="text-slate-400"> as {f.alias}</span>}
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={async () => {
                        if (!id) return;
                        const updated = await agentPlatformService.unbindFlow(id, f.id);
                        setAgent(updated);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <select
                  className={`flex-1 ${apTheme.input}`}
                  value={bindFlowId}
                  onChange={e => setBindFlowId(e.target.value)}
                >
                  <option value="">Selecione um fluxo…</option>
                  {allFlows.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.code})
                    </option>
                  ))}
                </select>
                <input
                  className={`w-32 ${apTheme.input}`}
                  placeholder="Alias"
                  value={bindFlowAlias}
                  onChange={e => setBindFlowAlias(e.target.value)}
                />
                <Button onClick={bindFlow} disabled={!bindFlowId}>
                  Vincular
                </Button>
              </div>
            </AgentPlatformPanel>

            <AgentPlatformPanel title="Bases de conhecimento">
              <ul className="divide-y divide-slate-100 mb-4">
                {agent.knowledgeBases.length === 0 && (
                  <li className="py-2 text-sm text-slate-500">Nenhuma base vinculada.</li>
                )}
                {agent.knowledgeBases.map(kb => (
                  <li key={kb.id} className="py-2 flex justify-between items-center text-sm">
                    <span className="text-slate-800">
                      {kb.knowledgeBaseName ?? kb.knowledgeBaseCode}
                      <span className="text-slate-400 ml-1">#{kb.priority}</span>
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={async () => {
                        if (!id) return;
                        const updated = await agentPlatformService.unbindKnowledgeBase(id, kb.id);
                        setAgent(updated);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <select
                  className={`flex-1 ${apTheme.input}`}
                  value={bindKbId}
                  onChange={e => setBindKbId(e.target.value)}
                >
                  <option value="">Selecione uma base…</option>
                  {allKnowledgeBases.map(kb => (
                    <option key={kb.id} value={kb.id}>
                      {kb.name} ({kb.code})
                    </option>
                  ))}
                </select>
                <Button onClick={bindKnowledgeBase} disabled={!bindKbId}>
                  Vincular
                </Button>
              </div>
            </AgentPlatformPanel>
          </div>
        )}

        {activeTab === 'grafo' && (
          <AgentPlatformPanel
            title="Grafo de orquestração"
            description={`Orquestração · v${graphMeta.version ?? '—'}${graphMeta.isPublished ? ' · publicado' : ''}`}
            actions={
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={graphViewMode === 'visual' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setGraphViewMode('visual')}
                >
                  Visual
                </Button>
                <Button
                  variant={graphViewMode === 'json' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setGraphViewMode('json')}
                >
                  JSON
                </Button>
                <Button variant="secondary" size="sm" onClick={validateGraph}>
                  Validar
                </Button>
                <Button size="sm" onClick={saveGraph} disabled={saving}>
                  Salvar
                </Button>
              </div>
            }
          >
            {validationErrors.length > 0 && (
              <ul className="mb-4 text-sm text-red-700 list-disc pl-5">
                {validationErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
            <div className={graphViewMode === 'visual' ? apTheme.studioCanvas : ''}>
              {graphViewMode === 'visual' ? (
                <AgentGraphVisualEditor definitionJson={graphJson} onChange={setGraphJson} />
              ) : (
                <JsonTextArea value={graphJson} onChange={setGraphJson} label="Definição JSON" rows={22} />
              )}
            </div>
          </AgentPlatformPanel>
        )}

        {activeTab === 'playground' && id && <AgentPlaygroundPanel agentId={id} />}

        {activeTab === 'canais' && (
          <AgentPlatformPanel
            title="Canais"
            description="Canais gravados na publicação. Roteamento por canal na execução será habilitado em fase posterior."
            className="max-w-xl"
          >
            <ul className="divide-y divide-slate-100">
              {agent.channels.length === 0 && (
                <li className="py-2 text-sm text-slate-500">Nenhum canal configurado na versão atual.</li>
              )}
              {agent.channels.map(c => (
                <li key={c.id} className="py-2 flex justify-between text-sm">
                  <span className="text-slate-800">{c.channel}</span>
                  <span className={c.isEnabled ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
                    {c.isEnabled ? 'Ativo' : 'Inativo'}
                  </span>
                </li>
              ))}
            </ul>
          </AgentPlatformPanel>
        )}
        </AgentPlatformShell>

      <AgentPublishModal
        isOpen={showPublishModal}
        agentName={agent.name}
        onConfirm={confirmPublish}
        onCancel={() => setShowPublishModal(false)}
        loading={publishing}
      />
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
